import React, { useEffect, useState } from "react";
import { Col, Modal } from "react-bootstrap";
import { FiRefreshCw, FiX } from "react-icons/fi";
import styled from "styled-components";
import LogButton from "../../../../../../components/atoms/LogButton";
import { postInvoiceRetry } from "../../../../../../api/modules";
import { errorToast, successToast } from "../../../../../../services/toastService";

const RETRY_DELAY_MS = 15 * 60 * 1000;
const RetryModal = styled(Modal)`
  .modal-content {
    border-radius: 15px;
    padding: 20px;
  }
`;

export const nextInvoiceRetryAt = (invoice) => {
  const lastAttempt = new Date(invoice.facturante_last_attempt_at || invoice.createdAt).getTime();
  if (!Number.isFinite(lastAttempt)) return Infinity;
  const lockUntil = new Date(invoice.facturante_dispatch_lock_until || 0).getTime();
  return Math.max(lastAttempt + RETRY_DELAY_MS, lockUntil || 0);
};

const PendingInvoiceRetry = ({ invoice, refetch }) => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [clock, setClock] = useState(Date.now());
  const [queuedUntil, setQueuedUntil] = useState(0);
  const availableAt = Math.max(nextInvoiceRetryAt(invoice), queuedUntil);

  useEffect(() => {
    setClock(Date.now());
    const delay = availableAt - Date.now();
    if (!Number.isFinite(delay) || delay <= 0) return;
    const timer = setTimeout(() => setClock(Date.now()), Math.min(delay + 50, 2147483647));
    return () => clearTimeout(timer);
  }, [availableAt]);

  const submitRetry = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await postInvoiceRetry(invoice._id);
      if (!result?.ok) {
        errorToast(result?.msg || "No se pudo programar el reintento.");
        return;
      }
      setQueuedUntil(new Date(result.next_retry_at).getTime() || Date.now() + RETRY_DELAY_MS);
      setShow(false);
      successToast(result.message || "Reintento programado.");
      refetch();
    } catch (error) {
      errorToast("No se pudo programar el reintento.");
    } finally {
      setBusy(false);
    }
  };

  if (invoice.status !== "pending") return <p>Pendiente...</p>;
  const lastAttempt = invoice.facturante_attempts?.[invoice.facturante_attempts.length - 1];

  return <>
    <p className="mb-1">Pendiente...</p>
    {clock >= availableAt && !invoice.invoice_number && !invoice.facturante_cae ? (
      <button type="button" className="btn btn-link p-0" style={{ fontSize: "0.82rem" }}
        title="Reintentar envio de la factura" onClick={() => setShow(true)}>
        <FiRefreshCw className="mr-1" /> Reintentar envio
      </button>
    ) : null}
    <RetryModal show={show} onHide={() => !busy && setShow(false)} centered>
      <Modal.Header className="justify-content-between">
        <h5 className="text-primary mb-0">Reintentar envio</h5>
        <button type="button" className="btn btn-link p-0 text-dark" title="Cerrar"
          aria-label="Cerrar" disabled={busy} onClick={() => setShow(false)}><FiX /></button>
      </Modal.Header>
      <Modal.Body>
        <p>Confirmar reintento de esta factura a Facturante.</p>
        {lastAttempt?.error_message ? <p className="text-muted mb-0">{lastAttempt.error_message}</p> : null}
      </Modal.Body>
      <Modal.Footer className="footer-modal">
        <Col className="d-flex justify-content-between p-0">
          <LogButton title="Cancelar" color="secondary" border="curve" disabled={busy} onClick={() => setShow(false)} />
          <LogButton title={busy ? "Enviando..." : "Reintentar envio"} color="primary"
            border="curve" disabled={busy} onClick={submitRetry} />
        </Col>
      </Modal.Footer>
    </RetryModal>
  </>;
};

export default PendingInvoiceRetry;
